import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { CONFIG } from 'variables/config';
import { authConfig, hasLinkedSheet } from 'services/integrations';
import { hostnameFromUrl } from 'services/hostname';
import { FormTheme, FormThemeUsage, ThemeUsageEntry, fetchThemes, updateThemeUsage } from 'services/formThemes';

interface PanelTemplate {
    _id: string;
    name: string;
    isUniversal?: boolean;
    landingPageUrls?: string[];
    themeId?: string;
    pageThemeIds?: Record<string, string>;
    sheetSyncEnabled?: boolean;
    googleSheetId?: string;
}

interface Selection {
    templateWide: boolean;
    pages: Set<string>;
}

const pathOf = (url: string, hostname: string) => url.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(hostname, '') || '/';

function TriStateCheckbox({ checked, indeterminate, onChange, label }: { checked: boolean; indeterminate: boolean; onChange: (checked: boolean) => void; label: string }) {
    const ref = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate;
    }, [indeterminate]);
    return <input ref={ref} type="checkbox" aria-label={label} checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 cursor-pointer accent-[#241C5C]" />;
}

/**
 * "Appliquer « thème » à…" — every integration of the seller, each with its sites and pages, to pick
 * where a theme applies in one go. Checked = uses this theme; unchecking a page that had it sends it
 * back to its integration's design. Pages on another theme say so, since checking them replaces it.
 * `focusTemplateId` lists that integration first, opened (from its Apparence tab).
 */
export default function ThemeApplyPanel({ theme, focusTemplateId, onClose, onApplied }: {
    theme: { _id: string; name: string };
    focusTemplateId?: string;
    onClose: () => void;
    onApplied: (usedBy: FormThemeUsage[]) => void;
}) {
    const [templates, setTemplates] = useState<PanelTemplate[] | null>(null);
    const [themes, setThemes] = useState<FormTheme[]>([]);
    const [selection, setSelection] = useState<Record<string, Selection>>({});
    const initial = useRef<Record<string, Selection>>({});
    const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
    const [query, setQuery] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        Promise.all([axios.get(`${CONFIG.apiUrl}/form-template`, authConfig()), fetchThemes()])
            .then(([res, themeList]) => {
                // A Google Sheet import has no form to style.
                const list: PanelTemplate[] = (res.data || []).filter((t: PanelTemplate) => t.isUniversal || !hasLinkedSheet(t as any));
                list.sort((a, b) => (a._id === focusTemplateId ? -1 : b._id === focusTemplateId ? 1 : 0));
                const sel: Record<string, Selection> = {};
                list.forEach((t) => {
                    sel[t._id] = {
                        templateWide: !!t.themeId && String(t.themeId) === theme._id,
                        pages: new Set(Object.entries(t.pageThemeIds || {}).filter(([, id]) => id === theme._id).map(([url]) => url)),
                    };
                });
                initial.current = sel;
                setSelection(sel);
                setThemes(themeList);
                setTemplates(list);
                // Sites start folded except on the integration the panel was opened from.
                const folded: Record<string, boolean> = {};
                list.forEach((t) => { if (t._id !== focusTemplateId && (t.landingPageUrls || []).length > 0) folded[t._id] = true; });
                setCollapsed(folded);
            })
            .catch(() => {
                toast.error('Impossible de charger vos formulaires');
                onClose();
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [theme._id]);

    const themeName = (id?: string) => (id ? themes.find((t) => t._id === String(id))?.name : undefined);

    const setTemplateWide = (templateId: string, value: boolean) =>
        setSelection((sel) => ({ ...sel, [templateId]: { ...sel[templateId], templateWide: value } }));

    const setPages = (templateId: string, urls: string[], value: boolean) =>
        setSelection((sel) => {
            const pages = new Set(sel[templateId].pages);
            urls.forEach((u) => (value ? pages.add(u) : pages.delete(u)));
            return { ...sel, [templateId]: { ...sel[templateId], pages } };
        });

    const changed = useMemo(() => {
        const entries: ThemeUsageEntry[] = [];
        for (const [templateId, sel] of Object.entries(selection)) {
            const before = initial.current[templateId];
            const samePages = before && before.pages.size === sel.pages.size && Array.from(sel.pages).every((p) => before.pages.has(p));
            if (!before || before.templateWide !== sel.templateWide || !samePages) {
                entries.push({ templateId, templateWide: sel.templateWide, pages: Array.from(sel.pages) });
            }
        }
        return entries;
    }, [selection]);

    const totals = useMemo(() => {
        const values = Object.values(selection);
        return { forms: values.filter((s) => s.templateWide).length, pages: values.reduce((n, s) => n + s.pages.size, 0) };
    }, [selection]);

    const apply = async () => {
        if (changed.length === 0) {
            onClose();
            return;
        }
        setSaving(true);
        try {
            const usedBy = await updateThemeUsage(theme._id, changed);
            toast.success(`Thème « ${theme.name} » appliqué`);
            onApplied(usedBy);
            onClose();
        } catch (error) {
            console.error('Error applying theme:', error);
            toast.error("Le thème n'a pas pu être appliqué");
            setSaving(false);
        }
    };

    const q = query.trim().toLowerCase();

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div className="flex max-h-[85vh] w-full max-w-[680px] flex-col overflow-hidden rounded-[18px] bg-white font-hanken text-[#1A1613] shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="border-b border-[#EDE7DE] px-6 py-5">
                    <h2 className="font-newsreader text-[24px] font-medium leading-tight text-[#14110E]">Appliquer « {theme.name} » à…</h2>
                    <p className="mt-1 text-[13.5px] text-[#6B6357]">
                        Un formulaire entier, ou seulement certaines pages. Une page sans thème suit le design de son formulaire.
                    </p>
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Rechercher une page"
                        className="mt-3 w-full rounded-[9px] border border-[#DDD5C9] px-3 py-2 text-[13.5px] outline-none focus:border-[#241C5C]"
                    />
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                    {!templates ? (
                        <p className="py-8 text-center text-[13.5px] text-[#8A8172]">Chargement…</p>
                    ) : templates.length === 0 ? (
                        <p className="py-8 text-center text-[13.5px] text-[#8A8172]">Aucun formulaire pour l'instant.</p>
                    ) : (
                        <div className="flex flex-col gap-4">
                            {templates.map((t) => {
                                const sel = selection[t._id];
                                if (!sel) return null;
                                const urls = (t.landingPageUrls || []).filter((u) => !q || u.toLowerCase().includes(q));
                                if (q && urls.length === 0 && !t.name.toLowerCase().includes(q)) return null;
                                const bySite = new Map<string, string[]>();
                                urls.forEach((u) => {
                                    const host = hostnameFromUrl(u);
                                    bySite.set(host, [...(bySite.get(host) || []), u]);
                                });
                                const otherTemplateTheme = t.themeId && String(t.themeId) !== theme._id ? themeName(t.themeId) : undefined;
                                const isOpen = !!q || !collapsed[t._id];
                                return (
                                    <div key={t._id} className="rounded-[14px] border border-[#EDE7DE]">
                                        <div className="flex items-center justify-between gap-3 bg-[#FAF8F4] px-4 py-3">
                                            <label className="flex min-w-0 cursor-pointer items-center gap-3">
                                                <input type="checkbox" checked={sel.templateWide} onChange={(e) => setTemplateWide(t._id, e.target.checked)} className="h-4 w-4 cursor-pointer accent-[#241C5C]" />
                                                <span className="min-w-0">
                                                    <span className="block truncate text-[14px] font-semibold text-[#14110E]">Tout le formulaire « {t.name} »</span>
                                                    {otherTemplateTheme && (
                                                        <span className="text-[12px] text-[#8A8172]">{sel.templateWide ? 'remplace' : 'thème actuel :'} « {otherTemplateTheme} »</span>
                                                    )}
                                                </span>
                                            </label>
                                            {bySite.size > 0 && !q && (
                                                <button type="button" onClick={() => setCollapsed((c) => ({ ...c, [t._id]: !c[t._id] }))} className="flex-shrink-0 text-[12.5px] font-semibold text-[#241C5C] hover:underline">
                                                    {isOpen ? 'Masquer les pages' : `${urls.length} page${urls.length > 1 ? 's' : ''}`}
                                                </button>
                                            )}
                                        </div>
                                        {isOpen && bySite.size > 0 && (
                                            <div className="divide-y divide-[#EDE7DE] px-4">
                                                {Array.from(bySite.entries()).map(([host, siteUrls]) => {
                                                    const checkedCount = siteUrls.filter((u) => sel.pages.has(u)).length;
                                                    return (
                                                        <div key={host} className="py-2.5">
                                                            <label className="flex cursor-pointer items-center gap-3">
                                                                <TriStateCheckbox
                                                                    label={`Toutes les pages de ${host}`}
                                                                    checked={checkedCount === siteUrls.length}
                                                                    indeterminate={checkedCount > 0 && checkedCount < siteUrls.length}
                                                                    onChange={(value) => setPages(t._id, siteUrls, value)}
                                                                />
                                                                <span className="text-[13px] font-semibold text-[#3B352C]">{host}</span>
                                                                <span className="text-[12px] text-[#A79E90]">{checkedCount}/{siteUrls.length}</span>
                                                            </label>
                                                            <div className="mt-1.5 flex flex-col gap-1 pl-7">
                                                                {siteUrls.map((u) => {
                                                                    const checked = sel.pages.has(u);
                                                                    const other = t.pageThemeIds?.[u] && t.pageThemeIds[u] !== theme._id ? themeName(t.pageThemeIds[u]) : undefined;
                                                                    return (
                                                                        <label key={u} className="flex cursor-pointer items-center gap-3" title={u}>
                                                                            <input type="checkbox" checked={checked} onChange={(e) => setPages(t._id, [u], e.target.checked)} className="h-3.5 w-3.5 cursor-pointer accent-[#241C5C]" />
                                                                            <span className="truncate text-[13px] text-[#6B6357]">{pathOf(u, host)}</span>
                                                                            {other && (
                                                                                <span className={`flex-shrink-0 rounded-full px-2 py-[1px] text-[11px] font-semibold ${checked ? 'bg-[#FCF2D4] text-[#7A5A05]' : 'bg-[#F3EFE8] text-[#8A8172]'}`}>
                                                                                    {checked ? 'remplace' : 'thème'} « {other} »
                                                                                </span>
                                                                            )}
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#EDE7DE] px-6 py-4">
                    <span className="text-[13px] text-[#8A8172]">
                        {totals.forms} formulaire{totals.forms > 1 ? 's' : ''} entier{totals.forms > 1 ? 's' : ''} · {totals.pages} page{totals.pages > 1 ? 's' : ''}
                    </span>
                    <div className="flex gap-2">
                        <button type="button" onClick={onClose} className="rounded-[10px] px-4 py-2 text-[13.5px] font-semibold text-[#6B6357] hover:bg-[#FAF8F4]">Annuler</button>
                        <button
                            type="button"
                            onClick={apply}
                            disabled={saving || !templates}
                            className="rounded-[10px] bg-[#241C5C] px-4 py-2 text-[13.5px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                        >
                            {saving ? 'Application…' : changed.length === 0 ? 'Fermer' : 'Appliquer'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
